import { useInfiniteQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { PaginatedResponse } from "../types";

import { GetChangelogRequest, GetChangelogResponse } from "./types";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export const useGetChangelog = ({
  entity_id,
  entity_type,
  per_page = 50,
  include_unapproved = true,
  start_date,
  end_date,
  user_id,
  pending,
  enabled = true,
}: Omit<GetChangelogRequest, "page"> & {
  pending?: boolean;
  enabled?: boolean;
}) => {
  const { workspaceId } = useParams();
  const authContext = useAuth();

  return useInfiniteQuery<PaginatedResponse<GetChangelogResponse["items"], "items", string>>({
    queryKey: [
      "changelog",
      workspaceId,
      entity_id,
      entity_type,
      per_page,
      include_unapproved,
      start_date,
      end_date,
      user_id,
      pending,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const url = new URL(`${API_URL}/api/dashboard/v2/workspaces/${workspaceId}/changelog`);

      url.searchParams.set("entity_id", String(entity_id));
      url.searchParams.set("entity_type", entity_type);
      url.searchParams.set("page", pageParam.toString());
      url.searchParams.set("per_page", per_page.toString());
      url.searchParams.set("include_unapproved", include_unapproved.toString());

      if (start_date) {
        url.searchParams.set("start_date", start_date);
      }
      if (end_date) {
        url.searchParams.set("end_date", end_date);
      }
      if (user_id !== undefined && user_id !== null) {
        url.searchParams.set("user_id", user_id.toString());
      }
      if (pending !== undefined) {
        url.searchParams.set("pending", pending.toString());
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: jsonAuthHeaders(authContext?.userToken ?? ""),
      });

      return handleResponse(response);
    },
    getNextPageParam: lastPage => (lastPage.has_next ? lastPage.next_num : undefined),
    getPreviousPageParam: firstPage => (firstPage.has_prev ? firstPage.prev_num : undefined),
    enabled: enabled && !!authContext?.userToken && !!workspaceId && !!entity_id,
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    keepPreviousData: true,
  });
};
